import {
    CircularProgress,
    createStyles,
    Grid,
    makeStyles,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Typography
} from '@material-ui/core';
import React, { useEffect, useState } from 'react';
import { IAdminCustomerReferralsResponse } from '../../models/ICustomers';
import CustomerService from '../../services/customer/customer.service';
import Card from '../Card/Card.component';

const useStyles = makeStyles((theme) =>
    createStyles({
        summaryValue: {
            fontSize: 26,
            fontWeight: 700,
            marginBottom: theme.spacing(0.5),
        },
        summaryLabel: {
            color: 'rgba(255,255,255,0.72)',
        },
        message: {
            color: 'rgba(255,255,255,0.72)',
        },
        error: {
            color: '#ff8a80',
        }
    })
);

const formatTimestamp = (value?: string | null) => {
    if (!value) {
        return "Not available";
    }

    const date = new Date(value);
    if (Number.isNaN(date.getTime())) {
        return value;
    }

    return date.toLocaleString();
}

interface Props {
    referrerUserId?: string;
}

export default function ReferralReport({
    referrerUserId
}: Props) {
    const classes = useStyles();
    const [data, setData] = useState<IAdminCustomerReferralsResponse | null>(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState("");

    useEffect(() => {
        if (!referrerUserId) {
            setIsLoading(false);
            return;
        }

        let isMounted = true;

        setIsLoading(true);
        setError("");

        CustomerService.getAdminCustomerReferrals(referrerUserId)
            .then((response) => {
                if (!isMounted) {
                    return;
                }

                setData(response);
            })
            .catch((nextError) => {
                if (!isMounted) {
                    return;
                }

                setError(nextError instanceof Error ? nextError.message : "Unable to load referral report.");
            })
            .finally(() => {
                if (isMounted) {
                    setIsLoading(false);
                }
            });

        return () => {
            isMounted = false;
        };
    }, [referrerUserId]);

    if (isLoading) {
        return (
            <Card>
                <Grid container justify="center">
                    <CircularProgress color="primary" />
                </Grid>
            </Card>
        );
    }

    if (error) {
        return (
            <Card>
                <Typography className={classes.error}>
                    {error}
                </Typography>
            </Card>
        );
    }

    return (
        <>
            <Card>
                <Grid container spacing={3}>
                    <Grid item xs={12} md={6}>
                        <Typography className={classes.summaryValue}>
                            {data?.activeReferralCount || 0}
                        </Typography>
                        <Typography className={classes.summaryLabel}>
                            Active Referrals
                        </Typography>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <Typography className={classes.summaryValue}>
                            {data?.totalReferralSignups || 0}
                        </Typography>
                        <Typography className={classes.summaryLabel}>
                            Total Referral Signups
                        </Typography>
                    </Grid>
                </Grid>
            </Card>

            <TableContainer component={Card}>
                <Table aria-label="Referral report">
                    <TableHead>
                        <TableRow>
                            <TableCell>Name</TableCell>
                            <TableCell>Email</TableCell>
                            <TableCell>Subscription</TableCell>
                            <TableCell>Attributed</TableCell>
                            <TableCell>Activated</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data?.referrals?.length ? data.referrals.map((row) => (
                            <TableRow key={row.userId}>
                                <TableCell>{row.name || "Unknown"}</TableCell>
                                <TableCell>{row.email || "-"}</TableCell>
                                <TableCell>{row.subscriptionStatus || "expired"}</TableCell>
                                <TableCell>{formatTimestamp(row.attributedAt)}</TableCell>
                                <TableCell>{formatTimestamp(row.subscriptionActivatedAt)}</TableCell>
                            </TableRow>
                        )) : (
                            <TableRow>
                                <TableCell colSpan={5}>
                                    <Typography className={classes.message}>
                                        No referral signups found for this customer yet.
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </>
    )
}
